import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import EtiquetaProducto, {
  EtiquetaPreview,
  MEDIDA_POR_DEFECTO,
} from "./EtiquetaProducto";
import { getProductoById } from "../../../services/admin/productos";
import "./BarcodeLabelsModal.css";

/**
 * Impresion de etiquetas de codigo de barras.
 *
 * Pensado para impresora de rollo (Zebra / Dymo): cada etiqueta es una
 * pagina propia y el tamano de pagina se fija con @page para que coincida
 * con el rollo. El tamano es configurable porque no todos los rollos miden
 * lo mismo; 50.8 x 25.4 mm (2 x 1 pulgada) es el mas comun.
 */

const MEDIDAS_PREDEFINIDAS = [
  { alias: "10.1 x 5 cm", ancho: 101, alto: 50 },
  { alias: "3 x 2 in", ancho: 76, alto: 51 },
  { alias: "2.25 x 1.25 in", ancho: 57, alto: 32 },
  { alias: "2 x 1 in", ancho: 50.8, alto: 25.4 },
  { alias: "1.5 x 1 in", ancho: 38, alto: 25.4 },
];

// Los mm se escriben sin decimales cuando son redondos: "101", no "101.0".
const mm = (n) => String(Number(n));

const plural = (n, palabra) => `${n} ${palabra}${n === 1 ? "" : "s"}`;

// Recuerda la ultima configuracion usada. Sin esto el modal volvia a
// 101 x 50 cada vez que se abria, y una tanda de etiquetas chicas obligaba
// a reelegir la medida en cada impresion.
const CLAVE_PREFS = "etiquetas_prefs";

// Se sube cuando cambia la forma de lo guardado. La version 2 guardaba
// tambien que campos mostrar; ahora el contenido es fijo y solo queda la
// medida del papel.
const VERSION_PREFS = 3;

const leerPrefs = () => {
  try {
    const guardado = JSON.parse(localStorage.getItem(CLAVE_PREFS));
    if (!guardado || typeof guardado !== "object") return null;

    const ancho = Number(guardado.ancho);
    const alto = Number(guardado.alto);

    // Se descarta lo que este fuera de rango: si quedo un valor invalido
    // guardado, mejor volver al de por defecto que abrir roto.
    if (!(ancho >= 20 && ancho <= 200) || !(alto >= 10 && alto <= 200)) return null;

    // Preferencias de una version anterior: se ignoran.
    if (Number(guardado.version) !== VERSION_PREFS) return null;

    return { ancho, alto };
  } catch {
    return null;
  }
};

/**
 * Etiquetas de codigo de barras.
 *
 * Dos modos, mismo componente para que el logo, el tamano y la impresion
 * sean identicos en los dos:
 *
 *   "inventario"  (por defecto) la etiqueta de bodega: nombre, P/N, S/N,
 *                 condicion, fecha de certificado, cantidad en stock y
 *                 ubicacion.
 *   "pedido"      la de una orden de venta: nombre, P/N, S/N y la cantidad
 *                 comprada. La condicion y la ubicacion no van: describen la
 *                 pieza en bodega, no lo que se despacha.
 *
 * En modo pedido, cada producto trae su cantidad en `cantidad`.
 */
function BarcodeLabelsModal({
  isOpen,
  onClose,
  productos = [],
  modo = "inventario",
}) {
  const esPedido = modo === "pedido";
  const prefs = useMemo(() => leerPrefs(), []);

  const [ancho, setAncho] = useState(prefs?.ancho ?? MEDIDA_POR_DEFECTO.ancho);
  const [alto, setAlto] = useState(prefs?.alto ?? MEDIDA_POR_DEFECTO.alto);

  // El contenido de la etiqueta es fijo: nombre, P/N, S/N, condicion, fecha
  // de certificado, cantidad, ubicacion, codigo y logo. Antes cada uno era
  // una casilla, pero es informacion que en bodega se necesita siempre, y
  // poder apagarla solo abria la puerta a imprimir una tanda incompleta.
  //
  // El precio no va: cambia, y una etiqueta con precio habria que
  // reimprimirla cada vez que cambia.

  // clave -> cuantas copias imprimir
  const [copias, setCopias] = useState({});

  /* Lo que se va a etiquetar. Un producto normal es una etiqueta; uno
     serializado es una por pieza, porque cada pieza tiene su propio codigo y
     su propio serial: es lo unico que permite distinguir cinco bombas del
     mismo numero de parte. */
  const [items, setItems] = useState([]);
  const [cargandoUnidades, setCargandoUnidades] = useState(false);

  // Serializados que no tienen ninguna pieza cargada todavia.
  const [sinUnidades, setSinUnidades] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    let vigente = true;

    (async () => {
      const serializados = productos.filter((p) => Number(p.serializado) === 1);

      /* Las unidades no vienen en el listado: se piden solo para los
         serializados que se eligieron, no para todo el catalogo. */
      let porProducto = {};
      if (serializados.length) {
        setCargandoUnidades(true);
        const fichas = await Promise.all(
          serializados.map((p) =>
            getProductoById(p.producto_id).catch(() => null)
          )
        );
        fichas.forEach((f, i) => {
          porProducto[serializados[i].producto_id] = Array.isArray(f?.unidades)
            ? f.unidades
            : [];
        });
      }

      if (!vigente) return;

      const salida = [];
      const vacios = [];

      productos.forEach((p) => {
        if (Number(p.serializado) !== 1) {
          salida.push({ ...p, _clave: `p${p.producto_id}` });
          return;
        }

        const piezas = porProducto[p.producto_id] || [];
        if (piezas.length === 0) vacios.push(p);

        piezas.forEach((u) => {
          salida.push({
            ...p,
            // Lo de la pieza pisa lo del producto: la etiqueta es de la pieza.
            _clave: `u${u.producto_unidad_id}`,
            es_unidad: true,
            codigo_barra: u.codigo_barra,
            numero_serie: u.numero_serie,
            condicion_nombre: u.condicion_nombre,
            certificado_nombre: u.certificado_nombre,
            fecha_certificado: u.fecha_certificado,
            // Una pieza es una: la cantidad del producto no aplica.
            cantidad_disponible: 1,
          });
        });
      });

      setSinUnidades(vacios);
      setItems(salida);
      setCopias(
        salida.reduce((acc, it) => {
          if (it.codigo_barra) acc[it._clave] = 1;
          return acc;
        }, {})
      );
      setCargandoUnidades(false);
    })();

    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, productos]);

  // Solo se puede imprimir lo que tiene codigo.
  const conCodigo = useMemo(() => items.filter((p) => p.codigo_barra), [items]);
  const sinCodigo = useMemo(() => items.filter((p) => !p.codigo_barra), [items]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Guarda la configuracion al cambiarla, no al imprimir: asi tambien se
  // recuerda si el usuario cierra el modal sin mandar nada.
  useEffect(() => {
    if (!isOpen) return;

    try {
      localStorage.setItem(
        CLAVE_PREFS,
        JSON.stringify({ version: VERSION_PREFS, ancho, alto })
      );
    } catch {
      // Sin localStorage (modo privado, cuota llena) se sigue igual: solo
      // se pierde el recordar la medida.
    }
  }, [isOpen, ancho, alto]);

  const setCopiasDe = (id, valor) => {
    const n = Math.min(Math.max(Number.parseInt(valor, 10) || 1, 1), 200);
    setCopias((prev) => ({ ...prev, [id]: n }));
  };

  /**
   * Una etiqueta por unidad.
   *
   * En bodega la unidad es lo que hay en stock; en una orden, lo que se
   * compro. Igualar al stock en una orden imprimiria etiquetas de piezas que
   * no se van a despachar.
   */
  const igualarALaCantidad = () => {
    const siguiente = {};
    conCodigo.forEach((p) => {
      /* Una pieza es una sola cosa: su etiqueta no se multiplica por el
         stock del numero de parte. */
      const unidades = p.es_unidad
        ? 1
        : Number(esPedido ? p.cantidad : p.cantidad_disponible) || 0;
      siguiente[p._clave] = Math.min(Math.max(unidades, 1), 200);
    });
    setCopias(siguiente);
  };

  // Un elemento por etiqueta a imprimir.
  const etiquetas = useMemo(() => {
    const salida = [];
    conCodigo.forEach((p) => {
      const veces = copias[p._clave] ?? 1;
      for (let i = 0; i < veces; i++) salida.push({ producto: p, indice: i });
    });
    return salida;
  }, [conCodigo, copias]);

  // El tamano de pagina no puede salir de una variable CSS, asi que se
  // inyecta la regla @page con los valores elegidos.
  const estilosImpresion = `
    @page {
      size: ${ancho}mm ${alto}mm;
      margin: 0;
    }

    @media print {
      body > *:not(.etq-portal) { display: none !important; }

      .etq-portal { display: block !important; }

      .etq-etiqueta {
        width: ${ancho}mm;
        height: ${alto}mm;
        page-break-after: always;
        break-after: page;
        border: none;
      }

      .etq-etiqueta:last-child {
        page-break-after: auto;
        break-after: auto;
      }
    }
  `;

  if (!isOpen) return null;

  const anchoValido = ancho >= 20 && ancho <= 200;
  const altoValido = alto >= 10 && alto <= 200;
  const puedeImprimir = etiquetas.length > 0 && anchoValido && altoValido;

  const medidaActiva = MEDIDAS_PREDEFINIDAS.find(
    (m) => m.ancho === ancho && m.alto === alto
  );

  const resumenMedida = mm(ancho) + " x " + mm(alto) + " mm";

  return (
    <>
      <style>{estilosImpresion}</style>

      <div className="etq-overlay" onMouseDown={onClose}>
        <div className="etq-modal" onMouseDown={(e) => e.stopPropagation()}>
          <div className="etq-header">
            <div className="etq-header-txt">
              <h2 className="etq-title">
                {esPedido ? "Print sales order labels" : "Print barcode labels"}
              </h2>
              <p className="etq-subtitulo">
                {plural(conCodigo.length, "product")}
                {" \u00b7 "}
                {plural(etiquetas.length, "label")}
              </p>
            </div>

            <button className="etq-close" onClick={onClose} aria-label="Close">
              &#10005;
            </button>
          </div>

          <div className="etq-body">
            {cargandoUnidades ? (
              <div className="etq-aviso">Loading units…</div>
            ) : null}

            {sinUnidades.length > 0 ? (
              <div className="etq-aviso">
                {sinUnidades.length} of the selected products are tracked by
                unit but have no units loaded yet, so there is nothing to label
                for them: {sinUnidades.map((p) => p.no_parte || p.nombre).join(", ")}.
              </div>
            ) : null}

            {sinCodigo.length > 0 ? (
              <div className="etq-aviso">
                {sinCodigo.length} of {items.length} labels have no barcode and
                cannot be printed. Products and units created before the barcode
                feature was added need it assigned first.
              </div>
            ) : null}

            {conCodigo.length === 0 ? (
              <div className="etq-vacio">
                Nothing to print: none of the selected products — or their
                units — has a barcode.
              </div>
            ) : (
              /* Los ajustes a la izquierda y la etiqueta a la derecha. Antes
                 iba todo en una sola columna: la mitad del ancho quedaba
                 vacia y habia que bajar hasta el final para ver que se
                 estaba por imprimir. */
              <div className="etq-cols">
                <div className="etq-col">
                  <section className="etq-grupo">
                    <span className="etq-grupo-titulo">Label size</span>

                    <div className="etq-medidas">
                      {MEDIDAS_PREDEFINIDAS.map((m) => {
                        const activa = m === medidaActiva;

                        return (
                          <button
                            key={m.alias}
                            type="button"
                            aria-pressed={activa}
                            className={
                              "etq-medida" + (activa ? " etq-medida-on" : "")
                            }
                            onClick={() => {
                              setAncho(m.ancho);
                              setAlto(m.alto);
                            }}
                          >
                            <span className="etq-medida-mm">
                              {mm(m.ancho)} x {mm(m.alto)} mm
                            </span>
                            <span className="etq-medida-alias">{m.alias}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* La medida a mano queda a la vista y no escondida
                        detras de un "Custom" del desplegable: los rollos que
                        no estan en la lista son comunes. */}
                    <div
                      className={
                        "etq-libre" + (medidaActiva ? "" : " etq-libre-on")
                      }
                    >
                      <span className="etq-libre-et">Custom</span>

                      <input
                        type="number"
                        step="0.1"
                        min="20"
                        max="200"
                        aria-label="Width in millimeters"
                        value={ancho}
                        onChange={(e) => setAncho(Number(e.target.value))}
                        className={anchoValido ? "" : "etq-invalido"}
                      />

                      <em className="etq-libre-x">x</em>

                      <input
                        type="number"
                        step="0.1"
                        min="10"
                        max="200"
                        aria-label="Height in millimeters"
                        value={alto}
                        onChange={(e) => setAlto(Number(e.target.value))}
                        className={altoValido ? "" : "etq-invalido"}
                      />

                      <em className="etq-libre-u">mm</em>
                    </div>
                  </section>

                  <section className="etq-grupo">
                    <div className="etq-lista-head">
                      <span className="etq-grupo-titulo">Copies</span>

                      <button
                        type="button"
                        className="etq-btn-texto"
                        onClick={igualarALaCantidad}
                        title={
                          esPedido
                            ? "One label per unit ordered"
                            : "One label per unit in stock"
                        }
                      >
                        {esPedido ? "Match order quantity" : "Match stock"}
                      </button>
                    </div>

                    <div className="etq-lista">
                      {conCodigo.map((p) => (
                        <div className="etq-fila" key={p._clave}>
                          <div className="etq-fila-info">
                            <span className="etq-fila-nombre" title={p.nombre}>
                              {p.nombre}
                              {/* Con varias piezas del mismo producto, el
                                  nombre se repite: el serial es lo unico que
                                  dice cual es cual. */}
                              {p.es_unidad && p.numero_serie ? (
                                <span className="etq-fila-serie">
                                  S/N {p.numero_serie}
                                </span>
                              ) : null}
                            </span>
                            <span className="etq-fila-codigo">
                              {p.codigo_barra}
                            </span>
                          </div>

                          <span className="etq-fila-stock">
                            {p.es_unidad
                              ? "1 unit"
                              : esPedido
                              ? (Number(p.cantidad) || 0) + " ordered"
                              : (Number(p.cantidad_disponible) || 0) +
                                " in stock"}
                          </span>

                          <input
                            type="number"
                            min="1"
                            max="200"
                            aria-label={"Copies of " + p.nombre}
                            className="etq-copias"
                            value={copias[p._clave] ?? 1}
                            onChange={(e) => setCopiasDe(p._clave, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </section>
                </div>

                <div className="etq-col etq-col-preview">
                  <span className="etq-grupo-titulo">Preview</span>

                  <div className="etq-preview-caja">
                    {anchoValido && altoValido ? (
                      <EtiquetaPreview
                        producto={conCodigo[0]}
                        ancho={ancho}
                        alto={alto}
                        modo={modo}
                      />
                    ) : (
                      <p className="etq-preview-error">
                        Width must be between 20 and 200 mm, and height between
                        10 and 200 mm.
                      </p>
                    )}
                  </div>

                  <p className="etq-preview-pie">
                    {resumenMedida}
                    {conCodigo.length > 1
                      ? " \u00b7 first of " + conCodigo.length + " products"
                      : ""}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="etq-footer">
            <span className="etq-footer-resumen">
              {puedeImprimir
                ? plural(etiquetas.length, "label") + " \u00b7 " + resumenMedida
                : ""}
            </span>

            <div className="etq-footer-botones">
              <button className="etq-btn-suave" onClick={onClose}>
                Cancel
              </button>

              <button
                className="etq-btn-primario"
                onClick={() => window.print()}
                disabled={!puedeImprimir}
                title={
                  puedeImprimir
                    ? "Send to printer"
                    : "Nothing to print, or the label size is out of range"
                }
              >
                Print {etiquetas.length > 0 ? "(" + etiquetas.length + ")" : ""}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Solo esto sale en el papel: una etiqueta por pagina. Va montado
          directamente en <body> para que la regla de impresion pueda
          ocultar todo lo demas sin ocultarlo tambien a el. */}
      {createPortal(
        <div className="etq-portal">
          {etiquetas.map(({ producto, indice }) => (
            <EtiquetaProducto
              key={`${producto._clave}-${indice}`}
              producto={producto}
              ancho={ancho}
              alto={alto}
              modo={modo}
            />
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

export default BarcodeLabelsModal;
